import React from 'react';

import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { LoadingIndicator } from '../../../components/atoms/loading-indicator';
import { useAuth } from '../../../hooks/use-auth';
import { useOnboardingStatus } from '../../../hooks/use-subscription-status';
import { useGetProfileQuery } from '../../../store/queries/users';

const GETTING_STARTED_PATH = '/user/getting-started';

/**
 * Route guard that requires:
 * 1. User to be authenticated
 * 2. User to have completed onboarding
 *
 * Newly onboarded users are routed to Getting Started until they either finish
 * it or dismiss it. Without this, the only path to Getting Started is a single
 * timed redirect on the plan generation page, so anyone who leaves that page
 * before their plan finishes generating can never reach it again.
 *
 * Does NOT check subscription — free users can pass through.
 * Premium content is gated at the page level via FeatureGate components.
 */
const OnboardedRoute = () => {
  const { isAuthenticated } = useAuth();
  const { isLoading, isOnboardingComplete } = useOnboardingStatus();
  const { data: profileData, isFetching } = useGetProfileQuery(undefined, { skip: !isAuthenticated });
  const { pathname } = useLocation();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <LoadingIndicator />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  if (!isOnboardingComplete) {
    return <Navigate to="/onboarding" />;
  }

  const hasSettledGettingStarted =
    profileData?.gettingStartedDismissed || profileData?.firstTimeProgress?.gettingStartedCompleted;

  // Finishing or skipping Getting Started navigates away as soon as the write
  // resolves, which is also the moment the profile is invalidated. Acting on
  // the copy being replaced would bounce the user straight back.
  if (profileData && !isFetching && !hasSettledGettingStarted && pathname !== GETTING_STARTED_PATH) {
    return <Navigate to={GETTING_STARTED_PATH} replace />;
  }

  return <Outlet />;
};

export default OnboardedRoute;
